import React from 'react';

/**
 * HubModelCard
 *
 * Renders a single Hugging Face Hub search hit in the Discover tab. Matches
 * the visual language of RecommendedModelCard so the "recognized recommended"
 * row and the "Hub search results" row look consistent.
 */
function formatCompact(n) {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

/** Split `org/model-name` on the first slash (Hub model id shape). */
function splitRepoId(id) {
  if (!id || typeof id !== 'string') {
    return { namespace: '', repoName: '' };
  }
  const i = id.indexOf('/');
  if (i <= 0) return { namespace: '', repoName: id.trim() };
  return {
    namespace: id.slice(0, i).trim(),
    repoName: id.slice(i + 1).trim(),
  };
}

function HubModelCard({ model, onCardClick }) {
  if (!model) return null;

  const handleClick = () => onCardClick?.(model);

  const repoLabel = model.id || `${model.author || ''}/${model.name || ''}`.replace(/^\//, '');
  const { namespace, repoName } = splitRepoId(repoLabel);
  const displayRepo = repoName || repoLabel;

  return (
    <div
      className="model-card grid-card active hub-model-card"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleClick();
      }}
      title={repoLabel}
    >
      <div className="model-card-header hub-model-card-header">
        <h3 className="model-card-title model-card-title--hub">
          {namespace ? (
            <>
              <span className="hub-model-card-line hub-model-card-line--provider">
                <span className="hub-model-card-namespace">{namespace}</span>
                <span className="hub-model-card-sep" aria-hidden="true">/</span>
              </span>
              <span className="hub-model-card-line hub-model-card-line--name">
                <span className="hub-model-card-repo">{displayRepo}</span>
              </span>
            </>
          ) : (
            <span className="hub-model-card-line hub-model-card-line--name">
              <span className="hub-model-card-repo">{displayRepo}</span>
            </span>
          )}
        </h3>
        {model.gated ? (
          <span className="model-card-status" style={{ opacity: 0.8 }}>Gated</span>
        ) : (
          <span className="model-card-status" style={{ opacity: 0 }} aria-hidden />
        )}
      </div>

      <div className="model-card-meta-row hub-model-card-meta">
        <span className="hub-model-card-stat" title="Downloads">
          <i className="fa-solid fa-download hub-model-card-stat-icon" aria-hidden />
          {formatCompact(model.downloads)}
        </span>
        <span className="hub-model-card-stat" title="Likes">
          <i className="fa-solid fa-heart hub-model-card-stat-icon" aria-hidden />
          {formatCompact(model.likes)}
        </span>
      </div>
    </div>
  );
}

export default HubModelCard;
